import { createListenerMiddleware, ListenerMiddlewareInstance, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import { startGenericPolling } from './utils';
import { startGlobalPolling, stopGlobalPolling, updateGlobalPollingData } from './globalPollingSlice';

type CreateGlobalPollingListenerType = (
  apolloClient: ApolloClient<NormalizedCacheObject>
) => ListenerMiddlewareInstance<unknown, ThunkDispatch<unknown, unknown, UnknownAction>, unknown>;

export const createGlobalPollingListener: CreateGlobalPollingListenerType = (
  apolloClient: ApolloClient<NormalizedCacheObject>
) => {
  const middleware = createListenerMiddleware();
  const pollingControllers = new Map<string, AbortController>();

  middleware.startListening({
    actionCreator: startGlobalPolling,
    effect: async (action, { dispatch }) => {
      const { id, config } = action.payload;

      if (pollingControllers.has(id)) {
        pollingControllers.get(id)?.abort();
        pollingControllers.delete(id);
      }

      const abortController = new AbortController();
      pollingControllers.set(id, abortController);

      try {
        await startGenericPolling(
          {
            apolloClient,
            ...config,
            onComplete: (data) => {
              dispatch(updateGlobalPollingData({ id, data }));
              dispatch(stopGlobalPolling({ id }));
              config.onComplete(data);
            },
            onError: (error) => {
              dispatch(stopGlobalPolling({ id, reason: error.message }));
              config.onError(error);
            },
          },
          abortController.signal
        );
      } catch (error) {
        if (!abortController.signal.aborted) {
          dispatch(stopGlobalPolling({ id, reason: 'Global polling failed' }));
        }
      }
    },
  });

  middleware.startListening({
    actionCreator: stopGlobalPolling,
    effect: async (action) => {
      const { id } = action.payload;

      if (pollingControllers.has(id)) {
        pollingControllers.get(id)?.abort();
        pollingControllers.delete(id);
      }
    },
  });

  return middleware;
};
